// DaisyDepositIntervalContractList.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { getEnvelopesWithGroup } from "../../api/getEnvelopesWithGroup";
import { getEnvelopeStatuses } from "../../api/getEnvelopeStatus";
import { EnvelopeStatusResponse } from "../../models/envelope.model";

import { options } from "./DaisyDepositInterval";
import { User } from "../../models/user.model";
import { Organization } from "../../models/organization.model";
import { DaisyContractDetail } from "../../types/daisyContractDetail";

import { getCompanyDetails } from "../../api/getCompanyDetails";
import { makeContract } from "../../api/makeContract";
import { currencyFormatter } from "../../utils/currencyFormatter";
import { getUserActiveCryptoAccount } from "../../api/getAccounts";

// ---- Beállítások a névszerinti szűréshez (ha nincs template_ref_id a válaszban)
const NAME_WITHDRAW_OPTIONS =
  process.env.REACT_APP_TEMPLATE_DAISY_WITHDRAW_OPTIONS_NAME ||
  "D.A.I.SY - Kifizetési opció választása";
const NAME_WITHDRAW_CAPITAL =
  process.env.REACT_APP_TEMPLATE_DAISY_WITHDRAW_CAPITAL_NAME ||
  "D.A.I.SY - Tőkevisszafizetés";

const radioResponseMap = [
  { originalResponse: "Radio1", forUs: "1. opció", interestRateDate: new Date("2025-10-01") },
  { originalResponse: "Radio2", forUs: "2. opció", interestRateDate: new Date("2025-06-01") },
  { originalResponse: "Radio3", forUs: "3. opció", interestRateDate: new Date("2025-06-01") },
];

function monthsSince(date: Date): number {
  const now = new Date();
  let yearsDiff = now.getFullYear() - date.getFullYear();
  let monthsDiff = now.getMonth() - date.getMonth();
  let totalMonths = yearsDiff * 12 + monthsDiff;
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  if (now.getDate() >= endOfMonth) totalMonths += 1;
  return totalMonths < 0 ? 0 : totalMonths;
}

// ---- Helpers (védett olvasás)
const getValue = (env: EnvelopeStatusResponse, name: string): string =>
  env.envelopeFormData?.formData?.find((f: any) => f?.name === name)?.value ?? "";

const toNumber = (raw: string): number => {
  if (!raw) return 0;
  const cleaned = raw.toString().replace(/[^\d.-]/g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const getNumber = (env: EnvelopeStatusResponse, name: string): number => toNumber(getValue(env, name));

const getRadio = (env: any): string =>
  (env.radioValue as string | undefined) ?? getValue(env as EnvelopeStatusResponse, "radiogroup");

// ---- Kis UI elem
const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const style =
    status === "completed"
      ? "bg-green-100 text-green-800 border-green-200"
      : status === "sent"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : status === "delivered"
      ? "bg-blue-100 text-blue-800 border-blue-200"
      : status === "voided"
      ? "bg-red-100 text-red-800 border-red-200"
      : "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${style}`}>
      {status}
    </span>
  );
};

export default function DaisyDepositIntervalContractList() {
  const [envelopeStatus, setEnvelopeStatus] = useState<EnvelopeStatusResponse[]>([]);
  const [spinner, setSpinner] = useState(true);
  const [filterStatus, setFilterStatus] = useState<"all" | "completed" | "sent" | "delivered" | "voided">("all");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const envelopes = await getEnvelopesWithGroup();
        const refIds = envelopes.map((e) => e.envelope_ref_id);
        if (refIds.length === 0) {
          if (!cancelled) setEnvelopeStatus([]);
          return;
        }

        const statuses = await getEnvelopeStatuses(refIds, {
          formDataMode: "full",       // ezen a nézeten tényleg kell a teljes form
          concurrency: 8,
          includeRelations: true,     // és a relációk is kellenek (signer/group/template)
          skipIfDbStatusIn: ["voided"],   // << EZ a lényeg
        });

        const ok = statuses.filter((s) => !s.error);
        const sorted = ok.sort((a, b) => getRadio(a).localeCompare(getRadio(b)));
        if (!cancelled) setEnvelopeStatus(sorted as unknown as EnvelopeStatusResponse[]);
      } catch (err) {
        console.error(err);
        toast.error("Hiba a szerződések betöltése során.");
      } finally {
        if (!cancelled) setSpinner(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Szűrőhöz a válaszban lévő év-kategóriák
  const yearCategories = Array.from(
    new Set(
      envelopeStatus
        .map((env) => getValue(env, "year_category"))
        .filter(Boolean)
    )
  );

  const calculateRemainingAmount = (optionId: string, diff: number) => {
    if (optionId === "Radio1") return 0;
    if (optionId === "Radio2") return diff / 2;
    if (optionId === "Radio3") return diff;
    return 0;
  };

  // Szűrés státusz + csoport + template (név/ref, biztonságos)
  const filtered = envelopeStatus
    .filter((env) => {
      const statusMatch = filterStatus === "all" || env.envelope.status === filterStatus;
      const category = getValue(env, "year_category");
      const groupMatch = !selectedGroup || category === selectedGroup;
      return statusMatch && groupMatch;
    })
    .filter((e) => {
      // ha van explicit ref a .env-ben, azon is össze lehet vetni
      const wantRef = process.env.REACT_APP_TEMPLATE_DAISY_WITHDRAW_OPTIONS;
      const wantName = NAME_WITHDRAW_OPTIONS;
      const refId = e?.envelope?.template?.template_ref_id;
      const name = e?.envelope?.template?.template_name;

      if (wantRef && refId) return refId === wantRef;
      if (wantName && name) return name === wantName;
      if (!wantRef && !wantName) return true;
      return true; // védőháló
    });

  // Összesítők a három opcióról
  const radioAggregatesMap: Record<
    string,
    {
      count: number;
      diffSum: number;
      remainingSum: number;
      paidSum: number;
      diffWithInterestSum: number;
      remainingWithInterestSum: number;
      junInterest: number;
      octInterest: number;
    }
  > = {};

  filtered.forEach((env) => {
    const radio = getRadio(env);
    const diff = getNumber(env, "diff");
    if (!radio) return;

    const translated = radioResponseMap.find((r) => r.originalResponse === radio)?.forUs ?? radio;
    const remainingAmount = calculateRemainingAmount(radio, diff);
    let diffWithInterest = 0;

    if (!radioAggregatesMap[translated]) {
      radioAggregatesMap[translated] = {
        count: 0,
        diffSum: 0,
        remainingSum: 0,
        paidSum: 0,
        diffWithInterestSum: 0,
        remainingWithInterestSum: 0,
        junInterest: 0,
        octInterest: 0,
      };
    }

    if (radio === "Radio2") {
      const junDate = radioResponseMap.find((r) => r.originalResponse === radio)?.interestRateDate;
      const octDate = radioResponseMap.find((r) => r.originalResponse === "Radio1")?.interestRateDate;
      const octMonthsPassed = octDate ? monthsSince(octDate) : 0;
      const junMonthsPassed = junDate ? monthsSince(junDate) : 0;
      const octInterest = remainingAmount * 0.01 * octMonthsPassed;
      const junInterest = remainingAmount * 0.01 * junMonthsPassed;

      diffWithInterest = diff - remainingAmount + octInterest;
      const remainingAmountWithInterest = remainingAmount + junInterest;

      radioAggregatesMap[translated].remainingWithInterestSum += remainingAmountWithInterest;
      radioAggregatesMap[translated].junInterest += junInterest;
      radioAggregatesMap[translated].octInterest += octInterest;
    } else {
      const interestDate = radioResponseMap.find((r) => r.originalResponse === radio)?.interestRateDate;
      const monthsPassed = interestDate ? monthsSince(interestDate) : 0;
      const interest = diff * 0.01 * monthsPassed;
      diffWithInterest = diff + interest;

      if (radio === "Radio1") radioAggregatesMap[translated].octInterest += interest;
      if (radio === "Radio3") radioAggregatesMap[translated].junInterest += interest;
    }

    radioAggregatesMap[translated].count += 1;
    radioAggregatesMap[translated].diffSum += diff;
    radioAggregatesMap[translated].remainingSum += remainingAmount;
    radioAggregatesMap[translated].diffWithInterestSum += diffWithInterest;

    // "Már kifizetve" – ugyanazon signer e-mail alapján keresd a tőkevisszafizetés sablont
    const signerEmail = env.envelope.signer?.email;
    const hasCompletedCapitalContract = envelopeStatus.some(
      (e) =>
        e.envelope.signer?.email === signerEmail &&
        (e.envelope.template?.template_name === NAME_WITHDRAW_CAPITAL ||
          e.envelope.template?.template_ref_id === process.env.REACT_APP_TEMPLATE_DAISY_WITHDRAW_CAPITAL) &&
        e.envelope.status === "completed"
    );
    if (hasCompletedCapitalContract) {
      radioAggregatesMap[translated].paidSum += diff;
    }
  });

  // Szerződésindító adatok felépítése
  const buildContractDetail = (env: EnvelopeStatusResponse): DaisyContractDetail => {
    const yearCategory = getValue(env, "year_category");
    const diff = getNumber(env, "diff");
    const option = options.find((opt) => opt.name === yearCategory);

    return {
      year_category_id: option?.id ?? 0,
      year_category: yearCategory,
      until_date: getValue(env, "until_date"),
      deposit: getNumber(env, "deposit"),
      withdraw: getNumber(env, "withdraw"),
      diff,
      // ha nincs group_id a válaszban, próbáljuk meg group.group_id-t, különben 0
      group_id: (env as any).envelope?.group_id ?? (env as any).envelope?.group?.group_id ?? 0,
      remainingAmount: calculateRemainingAmount(getRadio(env), diff),
    };
  };

  // Szerződés indítása
  const processContract = async (
    user: User | undefined,
    org: Organization | undefined,
    currency: string,
    amount: number,
    envelope_ref_id: string | undefined,
    template_ref_id: string | undefined,
    daisyContractDetail?: DaisyContractDetail
  ) => {
    const companyDetails = await getCompanyDetails();
    const lendAmount = 0;

    try {
      toast.info("A szerződés előállítása megkezdődött, kérlek várj...");

      const accountData = user ? await getUserActiveCryptoAccount(user?.user_id) : null;
      
      await makeContract(
        user,
        org,
        companyDetails,
        currency,
        amount,
        lendAmount,
        template_ref_id,
        accountData,
        envelope_ref_id,
        daisyContractDetail
      ).then((res) => {
        setSigningUrl(res);
        setShowModal(true);
      }).catch((err: any) => {
        console.error(err);
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Gyors összegzés (ha szükséges)
  const radioDiffSumMap: Record<string, number> = {};
  filtered.forEach((env) => {
    const radio = getRadio(env);
    const diff = getNumber(env, "diff");
    if (!radio) return;
    const translated = radioResponseMap.find((r) => r.originalResponse === radio)?.forUs ?? radio;
    radioDiffSumMap[translated] = (radioDiffSumMap[translated] || 0) + diff;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-lg w-full">
            <h2 className="text-xl font-semibold mb-2 text-slate-800">
              A szerződés elkészült a DocuSign rendszerében.
            </h2>
            <p className="mb-6 text-sm text-slate-600">
              A zöld gomb megnyomásával lépjen tovább.
            </p>
            <div className="flex justify-end gap-3">
              <a
                href={signingUrl ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg bg-green-600 hover:bg-green-700 text-white px-4 py-2"
              >
                Tovább a DocuSign felületre
              </a>
              <button
                onClick={() => setShowModal(false)}
                className="inline-flex items-center justify-center rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 px-4 py-2"
              >
                Vissza
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fejléc + szűrők */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          className="rounded-lg px-3 py-2 bg-slate-900 text-slate-100 hover:bg-black"
          onClick={() => navigate("/daisyintervals")}
        >
          Vissza a kifizetésekhez
        </button>

        <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl px-3 py-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="statusFilter"
              value="all"
              checked={filterStatus === "all"}
              onChange={() => setFilterStatus("all")}
            />
            <span>Összes</span>
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="statusFilter"
              value="completed"
              checked={filterStatus === "completed"}
              onChange={() => setFilterStatus("completed")}
            />
            <span className="font-medium">Completed (Aláírva mindenki által)</span>
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="statusFilter"
              value="sent"
              checked={filterStatus === "sent"}
              onChange={() => setFilterStatus("sent")}
            />
            <span className="font-medium">Sent (Küldve, de nincs aláírva mindenki által)</span>
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="statusFilter"
              value="voided"
              checked={filterStatus === "voided"}
              onChange={() => setFilterStatus("voided")}
            />
            <span className="font-medium">Voided (Visszavonva / Törölve)</span>
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm ml-auto">
          <span className="text-slate-700">Kifizetési csoport:</span>
          <select
            className="border px-2 py-2 border-slate-300 rounded-lg bg-white"
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
          >
            <option value="">Összes csoport</option>
            {yearCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Összesítő kártyák */}
      {Object.entries(radioAggregatesMap).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm mb-6">
          <h2 className="text-base font-semibold text-slate-800 mb-4">Beérkezett válaszok összesítése</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(radioAggregatesMap)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([label, stats]) => (
                <div key={label} className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">{label}</h3>

                  <div className="space-y-1 text-sm text-slate-700 mb-4">
                    <p>
                      <span className="font-medium">Beérkezett válaszok:</span> {stats.count} db
                    </p>
                    <p>
                      <span className="font-medium">Kifizetésre váró összeg:</span>{" "}
                      {currencyFormatter(stats.diffSum, "EUR")}
                    </p>
                  </div>

                  <div className="space-y-1 text-sm text-slate-700 mb-4">
                    <p>
                      <span className="font-medium">
                        {label === "2. opció" ? "50% kifizetési tőke:" : "Kifizetési tőke:"}
                      </span>{" "}
                      {currencyFormatter(stats.remainingSum, "EUR")}
                    </p>
                    <p>
                      <span className="font-medium">Kamat szeptember után:</span>{" "}
                      {currencyFormatter(stats.octInterest, "EUR")}
                    </p>
                  </div>

                  <div className="space-y-1 text-sm text-slate-700 mb-4">
                    <p>
                      <span className="font-medium">
                        {label === "2. opció" ? "50% halasztott tőke:" : "Halasztott tőke:"}
                      </span>{" "}
                      {currencyFormatter(stats.diffWithInterestSum, "EUR")}
                    </p>
                    {(stats.junInterest > 0 || label === "2. opció") && (
                      <p>
                        <span className="font-medium">Halasztott tőke kamata júniustól:</span>{" "}
                        {currencyFormatter(stats.junInterest, "EUR")}
                      </p>
                    )}
                    {stats.remainingWithInterestSum > 0 && (
                      <p>
                        <span className="font-medium">Halasztott tőke összesen (kamattal):</span>{" "}
                        {currencyFormatter(stats.remainingWithInterestSum, "EUR")}
                      </p>
                    )}
                  </div>

                  <div className="text-sm text-green-800 font-medium">
                    Már kifizetve: {currencyFormatter(stats.paidSum, "EUR")}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Tartalom */}
      {spinner ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-2/3 mb-2" />
              <div className="h-3 bg-slate-200 rounded w-1/3 mb-4" />
              <div className="h-24 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-2xl bg-slate-50">
          <p className="text-slate-700 font-medium">Nincs megjeleníthető szerződés a jelenlegi szűrőkkel.</p>
          <p className="text-slate-500 text-sm mt-1">Próbálj másik státuszt vagy csoportot választani.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((env, index) => {
            const yearCategory = getValue(env, "year_category") || "-";
            const radioValue = getRadio(env) || "-";
            const depositValue = getNumber(env, "deposit");
            const diffValue = getNumber(env, "diff");
            const currency = env.envelope?.currency || "EUR";
            const sentAt = env.envelopeFormData?.sentDateTime;

            const radioHuman =
              radioResponseMap.find((r) => r.originalResponse === radioValue)?.forUs ?? radioValue;
            const radioLabel =
              radioResponseMap.find((r) => r.originalResponse === radioValue)?.originalResponse ?? radioValue;
            const remainingAmount = calculateRemainingAmount(radioLabel, diffValue);

            // e-mail alapján keressük meg az adott signerhez tartozó tőkevisszafizetési csomagokat
            const signerEmail = env.envelope.signer?.email;
            const capContracts = envelopeStatus.filter(
              (e) =>
                e.envelope.signer?.email === signerEmail &&
                (e.envelope.template?.template_name === NAME_WITHDRAW_CAPITAL ||
                  e.envelope.template?.template_ref_id === process.env.REACT_APP_TEMPLATE_DAISY_WITHDRAW_CAPITAL) &&
                e.status !== "voided"
            );
            const completedCapitalContracts = capContracts.filter(
              (e) => e.envelope.status === "completed"
            ).length;

            return (
              <div
                key={index}
                className={`${
                  completedCapitalContracts > 0 ? "bg-green-50" : "bg-white"
                } rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className="p-5 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-slate-900">
                      <span className="font-medium text-slate-600">Csoport:</span>{" "}
                      {env.envelope.group?.group_name ?? "Névtelen csoport"}
                    </h3>
                    <StatusPill status={env.envelope.status} />
                  </div>

                  {sentAt && (
                    <p className="text-sm text-slate-500">
                      <span className="font-medium">Küldve:</span>{" "}
                      {new Date(sentAt).toLocaleString()}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-700">
                    <p>
                      <span className="font-medium text-slate-600">Ügyfél:</span>{" "}
                      {env.envelope.signer?.name ?? "Nincs aláíró"}
                    </p>
                    <p className="text-right">
                      <span className="font-medium text-slate-600">Azonosító:</span>{" "}
                      <span className="font-mono text-xs">{env.envelope.envelope_ref_id}</span>
                    </p>
                    <p>
                      <span className="font-medium text-slate-600">Kifizetési csoport:</span> {yearCategory}
                    </p>
                    <p className="text-right">
                      <span className="font-medium text-slate-600">Template:</span>{" "}
                      {env.envelope.template?.template_name ??
                        env.envelope.template?.template_ref_id ??
                        "-"}
                    </p>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1">
                    <p className="text-sm text-slate-700">
                      <span className="font-medium text-slate-600">Befizetett:</span>{" "}
                      {currencyFormatter(depositValue, currency)}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium text-slate-600">Különbözet:</span>{" "}
                      {currencyFormatter(diffValue, currency)}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium text-slate-600">Beérkezett válasz:</span>{" "}
                      {radioHuman}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium text-slate-600">Fennmaradó összeg:</span>{" "}
                      {currencyFormatter(remainingAmount, currency)}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium text-slate-600">Hálózat:</span>{" "}
                      {env.envelope.signer?.network
                        ? `${env.envelope.signer.network}, ${env.envelope.signer?.network_address ?? ""}`
                        : "Nincs adat"}
                    </p>
                  </div>

                  {diffValue > 0 && (
                    <>
                      <button
                        onClick={() =>
                          processContract(
                            env.envelope.signer,
                            undefined,
                            currency,
                            getNumber(env, "diff"),
                            env.envelope.envelope_ref_id,
                            process.env.REACT_APP_TEMPLATE_DAISY_WITHDRAW_CAPITAL,
                            buildContractDetail(env)
                          )
                        }
                        className="mt-3 inline-flex items-center justify-center w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-medium"
                      >
                        Tőkevisszafizetési szerződés küldése
                      </button>

                      {capContracts.length > 0 && (
                        <div className="pt-2">
                          <p className="text-center text-sm text-slate-800 font-medium">
                            Ehhez a szerződéshez már küldtél ki {capContracts.length} db tőkevisszafizetésre
                            vonatkozó szerződést.
                          </p>

                          {completedCapitalContracts > 0 && (
                            <p className="text-center text-sm text-emerald-700 font-semibold">
                              A(z) {completedCapitalContracts} db tőkevisszafizetési szerződés már aláírásra került.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
